import { OfferT, ProductT, SkuT } from '@lambda/apis/src/catalog-v2/types';
import Image from 'next/image';
import { useContext, useState } from 'react';
import { Check } from 'lucide-react';
import { AppCtx } from 'pages/_app';
import { LineItemType } from '@lambda/apis/src/cart/types';
import classNames from 'classnames';
import Link from 'next/link';
import {
  trackAddToCart,
  trackRemoveFromCart,
} from '@/lib/analytics/trackProduct';
import { ProductSource } from '@/lib/analytics/types';
import { generateProductNotes } from '../../product/hooks/notes';
import { useCartContext } from '@/utils/hooks/cart/useCartContext';
import { priceFormater } from '@/settings/index';
import { priceFormatSuperscript } from '@/lib/price';
import QuantityStepper from '../common/QuantityStepper';
import i18n from '@/settings/i18n';

const t = i18n.init('cart');

const RecommendedCard = ({
  product,
  sku,
  offer,
  slotNumber,
  parentLineId,
  source = ProductSource.CART_RECOMMENDATIONS_SLIDER,
  size = 'sm',
  isAccessory = false,
}: {
  product: ProductT;
  sku: SkuT;
  offer: OfferT;
  slotNumber: number;
  parentLineId?: string;
  source?: ProductSource;
  /** `lg` for grids with room to spare, like the accessories page. */
  size?: 'sm' | 'lg';
  /**
   * Tag the line as an accessory when there's no `parentLineId` to tie it
   * to, so the cart doesn't take it for a device.
   */
  isAccessory?: boolean;
}) => {
  const [loading, setLoading] = useState(false);
  const { addToCart, getLineItemInCart, removeFromCart, updateCart } =
    useCartContext();
  const { zipcodeStateData } = useContext(AppCtx);

  const offerName = sku.title;

  const offerVariants = sku.title.split(' - ').slice(1).join(' - ');

  const lineItemInCart = getLineItemInCart(offer?.id);

  /**
   * One payload for everything this card does to the cart, so a recommended
   * accessory reports the same product properties and the same slot whether it
   * was added, re-counted or taken out again.
   *
   * `quantity` is the size of the change — how many were added or removed —
   * which is what Segment's `Product Added` and `Product Removed` mean by it,
   * not the quantity the line is left holding.
   */
  const trackQuantity = (
    track: typeof trackAddToCart,
    quantity: number,
  ): void =>
    track({
      v2: {
        product,
        sku,
        offer,
        isOutOfStock: false,
        slotNumber,
      },
      properties: {
        quantity,
        source,
      },
    });

  const onAddToCart = async () => {
    try {
      setLoading(true);

      // We don't pass express shipping or warranty here, as they are handled separately
      const notes = generateProductNotes({
        product,
        sku,
        offer,
        lineItem: getLineItemInCart(offer.id),
        zipcodeStateData,
        ...(parentLineId
          ? {
              type: 'CART_RECOMMENDATION' as const,
              recommendedFor: parentLineId,
            }
          : {}),
        ...(!parentLineId && isAccessory ? { type: 'ACCESSORY' as const } : {}),
      });

      await addToCart({
        typeId: offer.id,
        quantity: 1,
        notes,
        type: LineItemType.PRODUCT,
      }).then(async () => {
        trackQuantity(trackAddToCart, 1);
      });
    } finally {
      setLoading(false);
    }
  };

  const onRemove = async () => {
    if (!lineItemInCart) {
      return;
    }

    try {
      setLoading(true);

      // Read before the await: the line is gone once it resolves, and the
      // whole of it is what was removed.
      const quantityRemoved = lineItemInCart.quantity;

      // The last argument suppresses the hook's own Product Removed: this card
      // reports the removal itself, with the slider's attribution and the real
      // quantity, and two events for one removal would double-count it.
      await removeFromCart(
        lineItemInCart,
        undefined,
        undefined,
        undefined,
        true,
      );
      trackQuantity(trackRemoveFromCart, quantityRemoved);
    } finally {
      setLoading(false);
    }
  };

  const onQuantityChange = async (quantity: number) => {
    if (!lineItemInCart) {
      return;
    }

    if (quantity < 1) {
      await onRemove();

      return;
    }

    // Nothing asked for and nothing to report.
    if (quantity === lineItemInCart.quantity) {
      return;
    }

    const isIncrease = quantity > lineItemInCart.quantity;
    const quantityChanged = Math.abs(quantity - lineItemInCart.quantity);

    try {
      setLoading(true);
      await updateCart(lineItemInCart.id, { quantity });
      trackQuantity(
        isIncrease ? trackAddToCart : trackRemoveFromCart,
        quantityChanged,
      );
    } finally {
      setLoading(false);
    }
  };

  const productLink = `/collections/${encodeURIComponent(
    product.slug,
  )}?skuId=${encodeURIComponent(sku.id)}`;

  return (
    <div
      className={classNames(
        'relative flex h-full flex-col justify-between gap-6 bg-white transition-colors duration-150',
        // The accessories page's cards: tighter corners, no shadow (their panel
        // has it), and a 2px border once in the cart (an inset ring, so the
        // card doesn't shift). The cart slider keeps its own look.
        size === 'lg'
          ? 'rounded-lg border p-4'
          : 'rounded-2xl border p-3 shadow-[0_1px_2px_0_rgba(0,0,0,0.04)]',
        lineItemInCart
          ? classNames(
              'border-gray-700',
              size === 'lg' &&
                'shadow-[inset_0_0_0_1px_theme(colors.gray.700)]',
            )
          : 'border-[#E6E7EB]',
      )}
    >
      {/* The accessories page shows "in cart" by its border alone. */}
      {lineItemInCart && size !== 'lg' && (
        <span
          className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-gray-700 text-white"
          aria-label={t`Added to cart`}
        >
          <Check className="h-3 w-3" strokeWidth={3} />
        </span>
      )}
      <div className="text-sm leading-tight">
        <Link
          href={productLink}
          className={classNames(
            'mx-auto block transition-transform duration-150',
            size === 'lg'
              ? 'h-28 w-28 hover:scale-[1.06]'
              : 'h-24 w-24 hover:scale-[1.03]',
          )}
          target="_blank"
        >
          <Image
            // The card is for one SKU, so its own image (the colour shown in
            // the title) comes first; the product's only stands in for one.
            src={
              sku.images?.[0]?.url ??
              sku.image?.url ??
              product.images?.[0]?.url ??
              product.image?.url ??
              ''
            }
            alt={offerName}
            height={150}
            width={150}
            loading="lazy"
            className="h-full w-full object-contain"
          />
        </Link>
        <Link href={productLink} target="_blank" className="group">
          <h2
            // eslint-disable-next-line
            className={classNames(
              'two-line-ellipsis mt-4 text-[13px] group-hover:underline',
              size !== 'lg' && 'font-medium',
            )}
          >
            {offerName} {offerVariants ? `- ${offerVariants}` : ''}
          </h2>
        </Link>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span
          className={classNames(
            'font-bold',
            size === 'lg' ? 'text-[13px]' : 'text-sm',
          )}
          translate="no"
        >
          {priceFormatSuperscript(priceFormater.long(offer.price / 100))}
        </span>
        {lineItemInCart ? (
          <QuantityStepper
            quantity={lineItemInCart.quantity}
            max={offer?.stock}
            disabled={loading}
            onChange={onQuantityChange}
          />
        ) : (
          <button
            onClick={onAddToCart}
            className={classNames(
              'w-20 border border-[#1F232340] bg-white text-xs font-medium transition duration-150 ease-in-out enabled:hover:bg-gray-700 enabled:hover:text-white',
              // The accessories page's is as tall as the stepper it turns into.
              size === 'lg' ? 'h-8 rounded' : 'h-[35px] rounded-lg',
              { 'opacity-50': loading },
            )}
            disabled={loading}
          >
            {t`Add`}
          </button>
        )}
      </div>
    </div>
  );
};

export default RecommendedCard;
